import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// TS type
import { WindowCustomData } from 'page/detail/index';
import { Area, IGDSPlanProps, scrollToRef, IalreadyFetchedGDSMonth } from '@redux/common';
import { RootState } from '@redux/store';
import { ICommonStore } from '@redux/Common/interfaces';
import { ITHSRStore } from '@redux/THSR/interfaces';

// import module
import NvbRslb from '@magaele/nvb_rslb';

// component
import DetailInfo from './subComponent/DetailInfo';

// shared components
import Reload from '@svg/Reload'; // svg component
import MobileHeader from '@Common/mobileHeader';
import SlideFooter from '@component/Common/SlideFooter';
import Dialog from '@component/Common/Dialog';

// common function
import { fetchGDSDetailData, setGDSSessionData, fetchLightBoxData } from '@libs/apiCall';
import {
    convertTicketNum,
    decodeHTMLstring,
    addSeparator,
    getWindowCustomFunctions,
    getEarliestDate,
    getSessionInfo,
    handletBodyFixed,
    THSRClearStorage,
    checkLigBoxOrDialogDisplay,
    getAllDatesInDetailData,
} from '@libs/core';
import { isMobile } from '@libs/globalVariables';
import onSubmit from './Common/onSubmit';

// plugin
import cx from 'classnames';
import { useTranslation } from 'react-i18next';

// redux actions
import {
    setCalendar,
    setGDetail,
    setGTempCalendarData,
    setSession,
    setSessionPicked,
    setTicketNum,
    setTotalAmount,
    setGNowList,
    setGNowListData,
    setIsGDSDetailLoading,
    setGDSDetailFetchFail,
    setIsGDSSessionLoading,
    setGDSSessionFetchFail,
    setIsSubmitSuccess,
    setGCalenderErr,
    setGSessionErr,
    setGTicketErr,
    setScrollTo,
    setGReset,
} from '@redux/GDS/actions';
import { setFocusArea, setGDetailStore, setDetailFetchFailMsg } from '@redux/Common/actions';
import AddOnPriceTag from '@component/Common/AddOnPriceTag';

const GDSPlan: React.FC<IGDSPlanProps> = (props) => {
    const dispatch = useDispatch();
    const { formatPriceByCulture }: WindowCustomData = getWindowCustomFunctions();
    const { t } = useTranslation();
    const {
        FormatID,
        FormatName,
        LowListPrice,
        LowSalePrice,
        EarliestBuyDay,
        IntroductionArray,
        ExpectedUseDay,
        IsForSale,
        NotForSaleReason,
    } = props.data;
    const { Currency, SaleCurrency, listIndex, isCrossell, AdvanceTime } = props;
    const [openNvbRslb, setOpenNvbRslb] = useState<boolean>(null);
    const listBlockRef = useRef(null);
    const alreadyFetchedGDSMonth = useRef<IalreadyFetchedGDSMonth>({}); // 判斷切換月份是否打API的依據，2023/04/11 後取消使用(與IT討論，決定改成切換月分都會重打)
    const {
        GNowList,
        GNowListData,
        GDetail,
        calendar,
        ticketNum,
        sessionPicked,
        session,
        isGDSDetailLoading,
        GDSDetailFetchFail,
        isGDSSessionLoading,
        isSubmitSuccess,
        GTicketErr,
        GCalenderErr,
        GSessionErr,
    } = useSelector((state: RootState) => state.GDS);
    const {
        Foreign,
        ETID,
        isLogin,
        isDisney,
        GDetailStore,
        listStatus,
        GListData,
        DetailFetchFailMsg,
        VerificationStatus,
    }: ICommonStore = useSelector((state: RootState) => state.Common);
    const { THSRInitData }: ITHSRStore = useSelector((state: RootState) => state.THSR);
    const hasCalendar = GNowListData?.ExpectedUseDay;
    const hasSession = GDetail?.Sessions;
    const planCannotSell = !IsForSale || (ExpectedUseDay && !EarliestBuyDay);
    let daysArrInMonth = useMemo(() => getAllDatesInDetailData(GDetail, 'calendarDate'), [GDetail]);

    //新增當前月份到切換月份陣列 & 更新ETITEM

    const [dialogSwitch, setDialogSwitch] = useState<{ [key: string]: boolean }>({}); //Dialog-State object
    const [fetchLightBoxResult, setFetchLightBoxResult] = useState({}); //Fetch lightBox save result state

    // _start：迪士尼光箱開發區
    // TODO:迪士尼對話框光箱開發暫時設置的"假資料"，待後續等API完成，再行移除
    const lightBoxMockData: {
        [key: string]: {
            title?: string;
            content?: string;
        };
    }[] = [
        fetchLightBoxResult, //Fetch json
    ];
    const disneyLightBoxData = lightBoxMockData[0].disney;
    console.log('disneyLightBoxData', disneyLightBoxData);
    console.log('fetchLightBoxResult!', fetchLightBoxResult);
    console.log('dialogSwitch', dialogSwitch);
    // _END：迪士尼光箱開發區

    // Fetch LightBox object data & init dialog state
    useEffect(() => {
        const getLightBoxData = async () => {
            const getData = await fetchLightBoxData();
            const result = await getData.json();

            setFetchLightBoxResult(result);
        };
        getLightBoxData();

        /** 初始化 dialog state
         *  無法一開始就取得資料的光箱才在下面新增，其他等打完API後再在useEffect做資料確認&新增
         */
        setDialogSwitch({
            // 預設基本會有的光箱類型
            verify: false, // 驗價光箱
        });
    }, []);

    // Dialog component switch object settings
    useEffect(() => {
        /* 一開始就可以打API拿到資料的光箱 */
        // 迪士尼對話框光箱
        if (isDisney && !!disneyLightBoxData && Object.keys(disneyLightBoxData).length) {
            setDialogSwitch((prev) => ({ ...prev, disney: false }));
        }
    }, [isDisney, disneyLightBoxData, fetchLightBoxResult]);

    useEffect(() => {
        if (!GDetail) return;
        // 更新ETITEM
        if (ExpectedUseDay) {
            // 2023/04/11 與IT討論，決定改成切換月份都會重打，並且detail API -> EarliestBuyDay改成固定
            /*
            // 新增當前明細月份 判斷切換月份是否打API陣列
            let nowID =  GNowListData.FormatID;
            let date = dayjs(GDetail.EarliestBuyDay).format('YYYY-MM'); 
            let newObj;
            if(!alreadyFetchedGDSMonth.current[nowID]){
                // 新品項
                let arr = [date];
                newObj = {
                    [nowID] : arr
                }
                alreadyFetchedGDSMonth.current = {...newObj};
            }else{
                if(Object.values(alreadyFetchedGDSMonth.current[nowID]).findIndex((month) => month === date) < 0){
                    alreadyFetchedGDSMonth.current[nowID].push(date);
                }
            }
            // console.log('***alreadyFetchedGDSMonth.current', alreadyFetchedGDSMonth.current);
            */

            //沒場次的直接設定最早日的ETITEM，有場次的在setSession後，會在TicketBlock裡抓值重新render
            const firstDay = getEarliestDate(daysArrInMonth);
            if (!GDetail.Sessions) {
                daysArrInMonth.length !== 0
                    ? dispatch(
                          setTicketNum(convertTicketNum(GDetail.CalendarDate[firstDay]?.ETItem))
                      )
                    : dispatch(setTicketNum(convertTicketNum(GDetail.ETItem)));
            }
        } else {
            dispatch(setTicketNum(convertTicketNum(GDetail.ETItem)));
        }

        // 更新場次
        if (!GDetail.Sessions) {
            dispatch(setSession(null));
            dispatch(setSessionPicked(null));
        }
    }, [GDetail]);

    // PC設定是否預設展開
    useEffect(() => {
        // console.log('GListData.Format', GListData.Format);
        // console.log('listStatus', listStatus);
        if (!listStatus || !GListData || isMobile) return;
        if (THSRInitData?.HasTHSR) {
            if (listStatus.originQty !== 1) return;
            if (listStatus.onewayQty !== 0 || listStatus.roundQty !== 0) return;
        } else {
            if (GListData.Format?.length > 1) return;
        }

        openOption();
    }, [listStatus, THSRInitData, GListData]);

    // 檢查面版是否有加購價
    const [showCrossellTag, setShowCrossellTag] = useState(true);

    // 加購價省下的金額
    const [salePriceSpread, setSalePriceSpread] = useState(0);

    // 更新加總價格
    useEffect(() => {
        // 加總
        const calculateAmount = (clickedData: string) => {
            // const ETdata = ExpectedUseDay ? GDetail.CalendarDate[clickedData]?.ETItem : GDetail.ETItem;
            const ETdata = ExpectedUseDay
                ? GDetail.Sessions
                    ? getSessionInfo({ session, sessionPicked })?.ETItem
                    : GDetail.CalendarDate[clickedData]?.ETItem
                : GDetail.ETItem;
            if (!ETdata) return 0;

            const hasSalePriceSpread = ETdata.some(item => item.SalePrice_Spread > 0);
            setShowCrossellTag(hasSalePriceSpread);

            let totalAmount = 0;
            let totalSpread = 0;
            const keys = Object.keys(ticketNum);

            if (isLogin) {
                keys.map((key) => {
                    let obj = ETdata.find((item) => item.ItemSeq === key);
                    if (!obj) return [];
                    if (obj) {
                        let price = obj.SalePrice !== 0 ? obj.SalePrice : obj.SalePrice_NonMember;
                        if (isCrossell) {
                            totalSpread += ticketNum[key] * obj.SalePrice_Spread;
                            price -= obj.SalePrice_Spread;
                        }
                        totalAmount += ticketNum[key] * price;
                    }
                });
            } else {
                keys.map((key) => {
                    let obj = ETdata.find((item) => item.ItemSeq === key);
                    if (!obj) return [];
                    if (obj) {
                        let price = obj.SalePrice_NonMember;
                        if (isCrossell) {
                            totalSpread += ticketNum[key] * obj.SalePrice_Spread;
                            price -= obj.SalePrice_Spread;
                        }
                        totalAmount += ticketNum[key] * price;
                    }
                });
            }
            setSalePriceSpread(totalSpread);
            return totalAmount;
        };

        if (!!ticketNum) {
            const amount = calculateAmount(calendar);
            dispatch(setTotalAmount(addSeparator(formatPriceByCulture(Currency, amount)))); // 原本要使用Common裡面的Currency，但因為與list的Currency相同且已有此變數，故拿list的使用
        } else {
            dispatch(setTotalAmount('0'));
        }
    }, [ticketNum, isCrossell]);

    // 清單牌價顯示規則
    const ShouldOriginPriceShow = (() => {
        if (LowListPrice === 0 || !LowListPrice) return false;
        switch (Foreign) {
            case '0':
                if (LowSalePrice >= LowListPrice) return false;
                break;
            case '1':
            default:
                return true;
        }
        return true;
    })();

    // 打開方案
    const openOption = () => {
        //已售完的話就不要打開方案
        if (planCannotSell) return;
        const fetchDetailProps = {
            ETID,
            EarliestBuyDay,
            ExpectedUseDay,
            FormatID,
            isTHSR: false,
            context: {
                GDetailStore,
                setGDetailStore,
                setGDSDetailFetchFail,
                setDetailFetchFailMsg,
                setGDetail,
                setGTempCalendarData,
                setIsGDSDetailLoading,
                setCalendar,
                ThsrRoundType: null,
                setGSessionLoading: setIsGDSSessionLoading,
                setGSessionFetchFail: setGDSSessionFetchFail,
                setSession,
                setSessionPicked,
            },
            dispatch,
        };
        dispatch(setGNowListData(props.data));
        dispatch(setGNowList(FormatID));
        dispatch(setFocusArea(Area.Origin));
        fetchGDSDetailData(fetchDetailProps);
    };

    // M版用於滑動到特定區塊
    const handleScrollTo = () => {
        if (!isMobile) return;
        if (hasCalendar) {
            dispatch(setScrollTo(scrollToRef.calender));
        } else {
            dispatch(setScrollTo(scrollToRef.ticket));
        }
    };

    return (
        <div
            className={cx('plan', 'gdsplan', {
                opened: GNowList === FormatID,
                closed: GNowList !== FormatID,
            })}
            ref={listBlockRef}
            data-index={listIndex}
        >
            <div className="showInfo">
                <div className="leftPart">
                    <div className="title">{FormatName}</div>
                    {isMobile && !AdvanceTime && (
                        <div className="firstOrderDate">
                            {!ExpectedUseDay
                                ? ''
                                : EarliestBuyDay
                                ? `${t('noTHSR.plan.EarliestUseDay', {
                                      symbol: ':',
                                  })}${EarliestBuyDay}`
                                : t('noTHSR.plan.thisPlanIsSoldOut')}
                        </div>
                    )}
                    {IntroductionArray && (
                        <div className="notice_wrap">
                            <ul className="notice">
                                {IntroductionArray?.map((notice, i) => {
                                    const htmlStr = decodeHTMLstring(notice);
                                    return (
                                        <li key={i}>
                                            <span
                                                dangerouslySetInnerHTML={{ __html: htmlStr }}
                                            ></span>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    )}
                </div>
                <div className="rightPart">
                    <div className="pickBlock">
                        <div className="price">
                            <div className="sale">
                                {isCrossell && showCrossellTag && (
                                    <div className="addOnPrice">
                                        <AddOnPriceTag />
                                    </div>
                                )}
                                <span className="saleCurrency">{SaleCurrency}</span>
                                <span className="salePrice">{addSeparator(LowSalePrice)}</span>
                                <span className="priceLabel">{t('noTHSR.plan.up')}</span>
                            </div>
                            {/* 原價 */}
                            {ShouldOriginPriceShow && (
                                <div className="original">
                                    <span>{Currency}</span>
                                    <span>{addSeparator(LowListPrice)}</span>
                                </div>
                            )}
                        </div>
                        <div
                            className={cx('selectedBtn', {
                                disabled: isGDSDetailLoading || isGDSSessionLoading,
                                secondary:
                                    GNowList === FormatID &&
                                    !isGDSDetailLoading &&
                                    !isGDSSessionLoading,
                                allsold: planCannotSell,
                            })}
                            onClick={() => {
                                if (isGDSDetailLoading || isGDSSessionLoading || planCannotSell)
                                    return;
                                if (!isMobile && GNowList === FormatID) {
                                    dispatch(setGNowList(null));
                                    dispatch(setGNowListData(null));
                                    dispatch(setFocusArea(Area.None));
                                    return;
                                }
                                if (isMobile) {
                                    setOpenNvbRslb(true);
                                } else {
                                    // 滑動到點選的方案
                                    const listOpened = document.querySelector('.opened');
                                    const clickedRef = listBlockRef.current;
                                    const selectIndex = clickedRef.getAttribute('data-index');
                                    const OpenedIndex = listOpened?.getAttribute('data-index');
                                    if (listOpened && Number(selectIndex) > Number(OpenedIndex)) {
                                        // 如果新點選的方案在已展開方案的後面則先直接跳動到前一個方案再往下滑
                                        // 避免因要同時收合已展開方案造成高度計算錯誤而無法scroll到正確位置
                                        clickedRef.previousSibling?.scrollIntoView({
                                            behavior: 'auto',
                                        });
                                        setTimeout(() => {
                                            clickedRef.scrollIntoView({
                                                behavior: 'smooth',
                                            });
                                        }, 50);
                                    } else {
                                        // 其餘不會因收合造成計算高度錯誤的情況可直接smooth scroll
                                        clickedRef.scrollIntoView({
                                            behavior: 'smooth',
                                        });
                                    }
                                }

                                GCalenderErr && dispatch(setGCalenderErr(''));
                                GSessionErr && dispatch(setGSessionErr(''));
                                GTicketErr && dispatch(setGTicketErr(''));
                                DetailFetchFailMsg && dispatch(setDetailFetchFailMsg(null));
                                openOption();
                            }}
                        >
                            {isGDSDetailLoading ? (
                                <>
                                    <span>
                                        {planCannotSell
                                            ? t('noTHSR.plan.outOfStock')
                                            : NotForSaleReason}
                                    </span>
                                    {GNowList === FormatID && (
                                        <div className="reload">
                                            <Reload color="#f03742" />
                                        </div>
                                    )}
                                </>
                            ) : (
                                <>
                                    <span>
                                        {GNowList !== FormatID || isMobile
                                            ? planCannotSell
                                                ? NotForSaleReason
                                                : t('noTHSR.plan.choose', { particle: '' })
                                            : t('noTHSR.plan.choose_cancel')}
                                    </span>
                                </>
                            )}
                        </div>
                    </div>
                    {!isMobile && !AdvanceTime && (
                        <div className="firstOrderDate">
                            {!ExpectedUseDay
                                ? ''
                                : EarliestBuyDay
                                ? `${t('noTHSR.plan.EarliestUseDay', {
                                      symbol: ':',
                                  })}${EarliestBuyDay}`
                                : t('noTHSR.plan.thisPlanIsSoldOut')}
                        </div>
                    )}
                </div>
            </div>
            {!isMobile &&
                GNowList === FormatID &&
                !isGDSDetailLoading &&
                (GDSDetailFetchFail ? (
                    <div>
                        {DetailFetchFailMsg
                            ? t(`common.${DetailFetchFailMsg}`)
                            : t('common.defaultErrMsg')}
                    </div>
                ) : (
                    <DetailInfo
                        // alreadyFetchedGDSMonth={alreadyFetchedGDSMonth}
                        dialogSwitch={dialogSwitch}
                        setDialogSwitch={setDialogSwitch}
                        isCrossell={isCrossell}
                        salePriceSpread={salePriceSpread}
                        showCrossellTag={showCrossellTag}
                    />
                ))}
            {isMobile && (
                <NvbRslb
                    visible={openNvbRslb && !isGDSDetailLoading}
                    direction="right"
                    className={cx('gdsplan_slide', 'plansM')}
                >
                    <div className="mobileBlock">
                        <MobileHeader
                            title={t('noTHSR.plan.choose_plan')}
                            onLeftIconClick={() => {
                                handletBodyFixed(false);
                                setOpenNvbRslb(false);
                                dispatch(setGNowList(null));
                                dispatch(setGNowListData(null));
                                dispatch(setFocusArea(Area.None));
                            }}
                        />
                        <div className="contentWrap">
                            <div className="title">{FormatName}</div>
                            <div className="firstOrderDate">
                                {!ExpectedUseDay
                                    ? ''
                                    : EarliestBuyDay
                                    ? `${t('noTHSR.plan.EarliestUseDay', {
                                          symbol: ':',
                                      })}${EarliestBuyDay}`
                                    : t('noTHSR.plan.thisPlanIsSoldOut')}
                            </div>
                            {GNowList === FormatID ? (
                                GDSDetailFetchFail ? (
                                    <div>
                                        {DetailFetchFailMsg
                                            ? t(`common.${DetailFetchFailMsg}`)
                                            : t('common.defaultErrMsg')}
                                    </div>
                                ) : (
                                    <DetailInfo
                                        isCrossell={isCrossell}
                                        salePriceSpread={salePriceSpread}
                                        showCrossellTag={showCrossellTag}
                                        // alreadyFetchedGDSMonth={alreadyFetchedGDSMonth}
                                    />
                                )
                            ) : null}
                        </div>
                    </div>
                    {isMobile &&
                        !isGDSDetailLoading &&
                        !isGDSSessionLoading &&
                        !GDSDetailFetchFail && (
                            <SlideFooter
                                hide={!GNowList}
                                onClickLeftBtn={() => {
                                    dispatch(setGReset());
                                    handleScrollTo();
                                    hasSession &&
                                        daysArrInMonth.length !== 0 &&
                                        setGDSSessionData({
                                            date: getEarliestDate(daysArrInMonth), // 抓可選日期的第一天
                                            FormatID: GNowListData?.FormatID,
                                            ETID,
                                            context: {
                                                setGSessionLoading: setIsGDSSessionLoading,
                                                setGSessionFetchFail: setGDSSessionFetchFail,
                                                setSession,
                                                setSessionPicked,
                                            },
                                            dispatch,
                                        });
                                }}
                                onClickRightBtn={async () => {
                                    if (isSubmitSuccess || AdvanceTime?.length > 0) return;
                                    // 送出前清空高鐵相關sessionStorage
                                    THSRClearStorage();
                                    const isSuccess = await onSubmit('checkData');
                                    if (typeof isSuccess === 'undefined') return;
                                    if (isSuccess.selectChecked) {
                                        // 確認是否為迪士尼產品
                                        if (
                                            checkLigBoxOrDialogDisplay(
                                                dialogSwitch,
                                                setDialogSwitch,
                                                'disney'
                                            )
                                        ) {
                                            setDialogSwitch((prev) => {
                                                prev.disney = true;
                                                return { ...prev };
                                            });
                                        } else {
                                            dispatch(setIsSubmitSuccess(true));
                                            onSubmit('redirect');
                                        }
                                    } else if (
                                        !isSuccess.selectChecked &&
                                        'IsSuccess' in isSuccess.apiChecked &&
                                        !isSuccess.apiChecked.IsSuccess
                                    ) {
                                        // 驗價錯誤則顯示提示重整頁面光箱
                                        setDialogSwitch((prev) => {
                                            prev.verify = true;
                                            return { ...prev };
                                        });
                                    }
                                }}
                                leftBtnText={t('noTHSR.plan.allReset')}
                                rightBtnClass={[
                                    'planFoot',
                                    {
                                        notReady: AdvanceTime.length > 0,
                                        disabled: isSubmitSuccess,
                                    },
                                ]}
                                rightBtnJSX={
                                    <div className={cx('confirm')}>
                                        <div>
                                            {AdvanceTime.length > 0
                                                ? t('noTHSR.plan.comingSoon')
                                                : t('noTHSR.plan.bookNow')}
                                        </div>
                                        {isSubmitSuccess && (
                                            <div className="reload">
                                                <Reload color="#ffffff" />
                                            </div>
                                        )}
                                    </div>
                                }
                            />
                        )}
                </NvbRslb>
            )}
            {/* 迪士尼對話框光箱 */}
            <Dialog
                type="default"
                title={disneyLightBoxData?.title ?? ''}
                body={disneyLightBoxData?.content ?? ''}
                open={dialogSwitch.disney}
                onClose={() => {
                    console.log('Close Disney LightBox!');
                    dispatch(setIsSubmitSuccess(false));
                    setDialogSwitch((prev) => {
                        prev.disney = false;
                        return { ...prev };
                    });
                }}
                // onCancel={() => {
                //     console.log('cancel!')
                //     console.log(dialogSwitch)
                //     setDialogSwitch(prev => {
                //         prev.disney = false;
                //         return {...prev};
                //     })
                // }}
                onOK={() => {
                    console.log('Disney CheckOK!');
                    dispatch(setIsSubmitSuccess(true));
                    setDialogSwitch((prev) => {
                        prev.disney = false;
                        return { ...prev };
                    });
                    onSubmit('redirect');
                }}
                OKText={t('noTHSR.plan.IHaveRead')}
                showCloseButton={true}
                className={cx('disney')}
            />
            {/* 驗價提示光箱 */}
            <Dialog
                type="default"
                title={VerificationStatus?.Title ?? ''}
                body={VerificationStatus?.ErrMessage ?? 'fetch API error, please reset page'}
                // body={"購買品項 成人 / 每人，購買數量：0<br>小於最低購買數量：1 <br>請重新選擇"}
                open={dialogSwitch.verify}
                onClose={() => {
                    console.log('Close Verify LightBox!');
                    dispatch(setIsSubmitSuccess(false));
                    setDialogSwitch((prev) => {
                        prev.verify = false;
                        return { ...prev };
                    });
                }}
                onOK={() => {
                    // 按下已閱讀後重新整理頁面
                    console.log('validate error, reset detailPage...');
                    setDialogSwitch((prev) => {
                        prev.verify = false;
                        return { ...prev };
                    });
                    window.location.reload();
                }}
                OKText={t('noTHSR.plan.IHaveRead')}
                showCloseButton={true}
                className={cx('orderValidity')}
            />
        </div>
    );
};
export default GDSPlan;
